import { Box, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useReducer } from "react";
import * as React from "react";
import {
  useNavigate,
  useSearchParams,
  useLocation,
  useParams,
} from "react-router";

import { useFileTransfer } from "@/providers/FileTransfer";
import { AlertDialog } from "@/shared/components";
import { IngestAsAssetDialog } from "@/shared/components/visualization/IngestAsAssetDialog";
import { useRecentlyCompletedInvocations } from "@/shared/domain/actions/hooks/";
import { useDataset } from "@/shared/domain/datasets/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { type FileRecord } from "@/shared/domain/files";
import { SortColumn, SortDirection } from "@/shared/domain/files/FileService";
import {
  FileSystemNode,
  isDirectory,
  isFile,
} from "@/shared/domain/files/filesystem";
import {
  fileQueryKeys,
  useDeleteDirectoryMutation,
  useDeleteFileMutation,
  useDirectoryContents,
  useDirectoryExtensions,
  useDirectoryItemCount,
} from "@/shared/domain/files/hooks";
import {
  ErrorMonitoringService,
  useRegisterViewingContext,
} from "@/shared/services";

import { useDatasetContext } from "../useDatasetContext";

import { FileBrowserActionTypes, FileBrowserAction } from "./actions";
import { DatasetBreadcrumbs } from "./DatasetBreadcrumbs";
import { DatasetOperationBar } from "./DatasetOperationBar";
import { DragAndDropDisplay } from "./DragAndDropDisplay";
import { FileBrowserDndContext } from "./FileBrowserDndContext";
import { computeExtensionsArray } from "./helper";
import { fileBrowserReducer } from "./reducer";
import { FileBrowserState } from "./state";
import { useSearchTerm } from "./useSearchTerm";
import { useSortParams } from "./useSortParams";

const sliderValueFormat = (value: number) => {
  return `Zoom: ${value}`;
};

/**
 * Files tab of the dataset detail view.
 *
 * Renders the directory listing for the current dataset folder, along with
 * breadcrumb navigation, sort/filter controls, the upload button, and bulk
 * delete/selection affordances. The folder displayed is driven by the URL:
 * the `:datasetPath` parent route fixes the dataset, and the `files/*`
 * wildcard child route fixes the directory within it.
 */
export const DatasetFileBrowser: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { datasetId, org: currentOrg } = useDatasetContext();
  const { data: dataset } = useDataset(datasetId);
  const { sortColumn, sortDirection } = useSortParams();
  // The folder-within-dataset wildcard is matched on the `files/*` child
  // route and is not exposed through the parent `:datasetPath` outlet
  // context, so it must be pulled from useParams here.
  const { "*": rawFilePath } = useParams();

  // Trim whitespace and strip leading/trailing slashes so concatenating
  // this value into navigation URLs and API requests cannot produce
  // double slashes or empty path segments.
  const filePath = rawFilePath?.trim()?.replace(/^\/+|\/+$/g, "") ?? "";

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { beginFileUpload } = useFileTransfer();

  const [fileBrowserState, dispatch] = useReducer<
    React.Reducer<FileBrowserState, FileBrowserAction>
  >(fileBrowserReducer, {
    alert: {
      isOpen: false,
      title: "",
      text: "",
      action: async () => {},
    },
    activeToggle: "all",
    displayMode: (searchParams.get("display") as string) ?? "table",
    error: null,
    fileTypes: [],
    gridItems: 3,
    selectedItems: [],
    showHiddenFiles: false,
    tableConfig: {
      page: 0,
      rowsPerPage: 25,
      pageTokens: { 0: undefined },
    },
  });

  const [searchTerm, setSearchTerm] = useSearchTerm();

  // Hoisted so there is one wizard for the whole browser rather than one per
  // row, matching how the confirmation dialog above is arranged. The file it
  // holds is both the subject and the open/closed flag.
  const [ingestingFile, setIngestingFile] = React.useState<FileRecord | null>(
    null,
  );

  // The one place the ingest release gate is read. Withholding the handler,
  // rather than drilling a boolean through the five components between here and
  // the row menu, is what enforces it: the prop is optional the rest of the way
  // down, so TypeScript makes every call site account for its absence, and the
  // natural handling is the fall-through each already has (View shows the raw
  // file, a double click or tile click opens the file view).
  //
  // Nothing about *viewing* is gated. A workspace that already references a
  // generated asset keeps rendering it; this only stops new ones being made.
  const canIngestAssets = useCheckFeatureFlag("release-3d-assets");

  const currentToken =
    fileBrowserState.tableConfig?.pageTokens[fileBrowserState.tableConfig.page];

  const deleteFileMutation = useDeleteFileMutation();
  const deleteDirectoryMutation = useDeleteDirectoryMutation();

  const directoryExtensionsQuery = useDirectoryExtensions(
    filePath ?? "",
    datasetId,
  );

  const directoryContentsQuery = useDirectoryContents(
    filePath ?? "",
    datasetId,
    dataset?.org_id ?? currentOrg?.org_id,
    fileBrowserState.tableConfig.rowsPerPage,
    currentToken,
    searchTerm,
    fileBrowserState.showHiddenFiles,
    fileBrowserState.fileTypes,
    sortColumn,
    sortDirection,
  );

  useInvalidateDirectoryOnActionCompletion(datasetId);

  const currentPage = directoryContentsQuery.data?.items;
  const nextToken = directoryContentsQuery.data?.nextToken;
  React.useEffect(() => {
    if (directoryContentsQuery.error) {
      dispatch({
        type: FileBrowserActionTypes.SetError,
        payload: directoryContentsQuery.error,
      });
    }
  }, [directoryContentsQuery.error]);

  const itemCountQuery = useDirectoryItemCount(
    filePath ?? "",
    datasetId,
    dataset?.org_id ?? currentOrg?.org_id,
    searchTerm,
    fileBrowserState.fileTypes,
    fileBrowserState.showHiddenFiles,
  );

  const itemCount = itemCountQuery.data;

  const extensions = computeExtensionsArray(directoryExtensionsQuery.data);

  const handleRowDoubleClick = (row: FileSystemNode) => {
    if (isDirectory(row)) {
      let newPath = `${filePath ? filePath + "/" : ""}${row.name}`;

      const params = new URLSearchParams(searchParams);

      // Preserve sort state if not default
      if (!(sortColumn === "name" && sortDirection === "ASC")) {
        params.set("sortColumn", sortColumn);
        params.set("sortDirection", sortDirection);
      }

      newPath = `/datasets/${datasetId}/files/${newPath}?${params.toString()}`;

      void navigate(newPath);
    }
  };

  useEffect(() => {
    dispatch({
      type: FileBrowserActionTypes.SetTablePageToZero,
      payload: null,
    });
  }, [pathname]);

  const filePrefix = filePath;

  const handleGridSliderChange = (
    _event: Event,
    newValue: number | number[],
  ) => {
    if (typeof newValue === "number") {
      dispatch({
        type: FileBrowserActionTypes.SetGridItems,
        payload: newValue,
      });
    }
  };

  function handleActionConfirmation(
    title: string,
    text: string,
    action: () => Promise<void>,
  ) {
    dispatch({
      type: FileBrowserActionTypes.SetAlertState,
      payload: {
        isOpen: true,
        title: title,
        text: text,
        action,
      },
    });
  }

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!datasetId || !dataset) {
      return;
    }

    await beginFileUpload(event, dataset.org_id, datasetId, filePrefix);
    await directoryContentsQuery.refetch();
  };

  function summarizeItems(items: FileSystemNode[]): string {
    const hasFiles = items.some(isFile);
    const hasDirectories = items.some(isDirectory);

    if (items.length > 1) {
      if (hasFiles && hasDirectories) {
        // Mixed case, using a general term
        return "Items";
      }
      return hasFiles ? "Files" : "Directories";
    }

    return isFile(items[0]) ? "File" : "Directory";
  }

  const selectedFiles = React.useMemo(() => {
    return fileBrowserState.selectedItems
      .filter(isFile)
      .map((item) => item.file);
  }, [fileBrowserState.selectedItems]);

  useRegisterViewingContext("dataset", {
    selectedFiles: selectedFiles,
  });

  const handleSort = (column: SortColumn, direction: SortDirection) => {
    // Update URL parameters to reflect sort state
    setSearchParams(
      (oldParams) => {
        const params = new URLSearchParams(oldParams);

        params.set("sortColumn", column);
        params.set("sortDirection", direction);

        return params;
      },
      { replace: true },
    );
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          mt: theme.spacing(0),
          mb: theme.spacing(1.5),
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DatasetOperationBar
          datasetId={datasetId}
          extensions={extensions}
          fileTypes={fileBrowserState.fileTypes}
          display={fileBrowserState.displayMode}
          activeToggle={fileBrowserState.activeToggle}
          handleViewToggle={(display, fileTypes, activeToggle) => {
            dispatch({
              type: FileBrowserActionTypes.HandleViewToggle,
              payload: { display, fileTypes, activeToggle },
            });
            setSearchParams(
              (oldParams) => {
                const params = new URLSearchParams(oldParams);
                params.set("display", display);
                return params;
              },
              { replace: true },
            );
          }}
          handleDeleteItems={(items) => {
            if (items.length === 0) {
              return;
            }

            const deleteTerm = summarizeItems(items);
            const deleteTitle = `Delete ${deleteTerm}`;
            handleActionConfirmation(
              `${deleteTitle}`,
              `Are you sure you want to delete ${items.length} ${deleteTerm.toLowerCase()}?`,
              async function () {
                try {
                  const deletePromises = [];

                  const directoryPaths = items
                    .filter(isDirectory)
                    .map((item) => item.directory.relative_path);

                  if (directoryPaths.length > 0) {
                    deletePromises.push(
                      deleteDirectoryMutation.mutateAsync({
                        datasetId: datasetId,
                        directoryPaths,
                      }),
                    );
                  }

                  const fileDeletePromises = items
                    .filter(isFile)
                    .map((item) => {
                      return deleteFileMutation.mutateAsync({
                        file: item.file,
                        resourceOwnerId: item.file.org_id,
                      });
                    });

                  deletePromises.push(...fileDeletePromises);

                  await Promise.all(deletePromises);

                  await directoryContentsQuery.refetch();
                  await itemCountQuery.refetch();

                  dispatch({
                    type: FileBrowserActionTypes.DeleteFiles,
                    payload: items,
                  });
                } catch (e) {
                  ErrorMonitoringService.captureError(e);
                }
              },
            );
          }}
          handleDeselectAll={() => {
            dispatch({
              type: FileBrowserActionTypes.SetSelectedItems,
              payload: [],
            });
          }}
          handleFileTypeSelection={(fileTypes) => {
            dispatch({
              type: FileBrowserActionTypes.SetFileTypes,
              payload: fileTypes,
            });
          }}
          handleFileSearch={(searchTerm) => {
            setSearchTerm(searchTerm);
            dispatch({
              type: FileBrowserActionTypes.SetSearchTerm,
              payload: searchTerm,
            });
          }}
          handleShowHiddenFilesToggle={() => {
            dispatch({
              type: FileBrowserActionTypes.SetShowHiddenFiles,
              payload: !fileBrowserState.showHiddenFiles,
            });
          }}
          showHiddenFiles={fileBrowserState.showHiddenFiles}
          uploadButtonPressed={() => fileInputRef?.current?.click()}
          selectedItems={fileBrowserState.selectedItems}
        />
      </Box>
      <div>
        <FileBrowserDndContext
          datasetId={datasetId}
          dispatch={dispatch}
          filePath={filePath}
          selectedItems={fileBrowserState.selectedItems}
        >
          <DatasetBreadcrumbs
            datasetId={datasetId}
            filePaths={filePath}
            displayMode={fileBrowserState.displayMode}
            gridItems={fileBrowserState.gridItems}
            handleDisplayToggle={(mode) => {
              if (mode !== null) {
                dispatch({
                  type: FileBrowserActionTypes.SetDisplayMode,
                  payload: mode,
                });
                setSearchParams(
                  (oldParams) => {
                    const params = new URLSearchParams(oldParams);
                    params.set("display", mode);
                    return params;
                  },
                  { replace: true },
                );
              }
            }}
            handleGridSliderChange={handleGridSliderChange}
            sliderValueFormat={sliderValueFormat}
          />
          <DragAndDropDisplay
            currentPage={currentPage}
            datasetId={datasetId}
            displayMode={fileBrowserState.displayMode}
            error={fileBrowserState.error}
            fileInputRef={fileInputRef}
            filePrefix={filePrefix}
            gridItems={fileBrowserState.gridItems}
            handleDeleteRow={(row) => {
              void directoryContentsQuery.refetch();

              dispatch({
                type: FileBrowserActionTypes.DeleteFiles,
                payload: [row],
              });
            }}
            handleRowDoubleClick={handleRowDoubleClick}
            handleSelectRows={(
              newRows: FileSystemNode[],
              isSelected: boolean,
            ) => {
              const selectedItemsMap: Map<string, FileSystemNode> = new Map();

              const getKey = (node: FileSystemNode) => {
                if (isFile(node)) {
                  return `${node.file.file_id}`;
                } else if (isDirectory(node)) {
                  return `${node.directory.relative_path}`;
                }
                return `${node.type}${node.name}`;
              };

              fileBrowserState.selectedItems.forEach((item) => {
                selectedItemsMap.set(getKey(item), item);
              });

              if (isSelected) {
                newRows.forEach((row) =>
                  selectedItemsMap.set(getKey(row), row),
                );
              } else {
                newRows.forEach((row) => selectedItemsMap.delete(getKey(row)));
              }

              const newItems = [...selectedItemsMap.values()];

              dispatch({
                type: FileBrowserActionTypes.SetSelectedItems,
                payload: newItems,
              });
            }}
            isPending={directoryContentsQuery.isPending}
            isFetching={directoryContentsQuery.isFetching}
            isPlaceholderData={directoryContentsQuery.isPlaceholderData}
            isSearching={Boolean(
              searchTerm || fileBrowserState.fileTypes.length > 0,
            )}
            onClearError={() => {
              dispatch({
                type: FileBrowserActionTypes.SetError,
                payload: null,
              });
            }}
            onChangeInput={handleFileUpload}
            onDropComplete={async () => {
              await directoryContentsQuery.refetch();
              await itemCountQuery.refetch();
            }}
            onPageChange={(_event, newPage) => {
              if (newPage > fileBrowserState.tableConfig.page) {
                dispatch({
                  type: FileBrowserActionTypes.IncrementTablePage,
                  payload: { newPage: newPage, nextToken: nextToken },
                });
              } else {
                dispatch({
                  type: FileBrowserActionTypes.DecrementTablePage,
                  payload: { newPage: newPage },
                });
              }
            }}
            onRowsPerPageChange={(event) => {
              dispatch({
                type: FileBrowserActionTypes.UpdateTableRowsPerPage,
                payload: parseInt(event?.target.value || "", 10) as
                  | 10
                  | 25
                  | 50,
              });
            }}
            selectedItems={fileBrowserState.selectedItems}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            tableConfig={fileBrowserState.tableConfig}
            totalItemCount={itemCount}
            handleActionConfirmation={handleActionConfirmation}
            onIngestAsAsset={canIngestAssets ? setIngestingFile : undefined}
          />
        </FileBrowserDndContext>
      </div>
      <AlertDialog
        dialogOpen={fileBrowserState.alert.isOpen}
        handleClose={() => {
          dispatch({
            type: FileBrowserActionTypes.SetAlertState,
            payload: {
              isOpen: false,
              title: "",
              text: "",
              action: async () => {},
            },
          });
        }}
        dialogTitle={fileBrowserState.alert.title}
        dialogText={fileBrowserState.alert.text}
        dialogAction={fileBrowserState.alert.action}
      />
      <IngestAsAssetDialog
        file={ingestingFile}
        onClose={() => setIngestingFile(null)}
      />
    </div>
  );
};

function useInvalidateDirectoryOnActionCompletion(datasetId: string) {
  const queryClient = useQueryClient();
  const recentlyCompletedQuery = useRecentlyCompletedInvocations(datasetId);

  const seenInvocationIdsRef = React.useRef<Set<string>>(new Set());

  const completedInvocationIds = React.useMemo(() => {
    return new Set(
      recentlyCompletedQuery.data?.items.map(
        (record) => record.invocation_id,
      ) ?? [],
    );
  }, [recentlyCompletedQuery.data?.items]);

  React.useEffect(() => {
    const seenIds = seenInvocationIdsRef.current;
    const newCompletions = [...completedInvocationIds].filter(
      (id) => !seenIds.has(id),
    );

    // If we found new completions, invalidate cache
    if (newCompletions.length > 0) {
      void queryClient.invalidateQueries({
        queryKey: fileQueryKeys.directoryContents({ datasetId }),
        exact: false,
      });
    }

    // Update seen IDs (keep only current ones to prevent memory leaks)
    seenInvocationIdsRef.current = new Set(completedInvocationIds);
  }, [datasetId, completedInvocationIds, queryClient]);
}
